/**
 * Read-only platform session calls. SK Notification owns no auth UI — the
 * platform auth app owns login/signup/verify/reset (redirect-handoff). Cookies
 * (accessToken + refreshToken) are httpOnly and never touched here.
 *
 * Hybrid token: platform auth rides the httpOnly cookie, but sk-notification-api
 * (:3005) reads the token from Authorization: Bearer only — so fetchAccessToken()
 * reads the current access token for the notification client to attach.
 */
import axiosInstance from '../../api/axiosInstance';
import {
  Platform_Me_Url,
  Platform_Logout_Url,
  Platform_Verify_Email_Url,
  Platform_Switch_Org_Url,
  Platform_Token_Url,
} from '../../api/endpoints';
import { Token_Handler } from '../../storage/tokenHandler';

const ME_TTL = 5_000;
let _meCache: { data: unknown; at: number } | null = null;
let _meInflight: Promise<unknown> | null = null;

export const invalidateMeCache = (): void => { _meCache = null; };

/** GET /v1/me — identity probe (5s cache + in-flight dedupe). */
export const getCurrentUser = async (): Promise<unknown> => {
  if (_meCache && Date.now() - _meCache.at < ME_TTL) return _meCache.data;
  if (!_meInflight) {
    _meInflight = axiosInstance
      .get(Platform_Me_Url)
      .then((r) => { _meCache = { data: r.data, at: Date.now() }; return r.data; })
      .finally(() => { _meInflight = null; });
  }
  return _meInflight;
};

/** GET /v1/auth/token — the current access token, for Bearer use against :3005.
 *  Platform returns { data: { token } }; tolerate access_token/top-level too. */
export const fetchAccessToken = async (): Promise<string> => {
  const { data } = await axiosInstance.get(Platform_Token_Url);
  const token = (data?.data?.token ?? data?.data?.access_token ?? data?.token ?? data?.access_token) as string | undefined;
  if (!token) throw new Error('fetchAccessToken: no access token returned');
  return token;
};

/** POST /v1/auth/logout — ends the session. */
export const logoutUser = async (): Promise<void> => {
  try {
    await axiosInstance.post(Platform_Logout_Url);
  } finally {
    invalidateMeCache();
    await Token_Handler.clearAll();
  }
};

/** POST /v1/auth/verify-email — consume the ?mode=verify&token= boot link. */
export const verifyEmail = async (token: string): Promise<unknown> => {
  const { data } = await axiosInstance.post(Platform_Verify_Email_Url, { token });
  return data;
};

/** POST /v1/auth/switch-org — re-mint cookies scoped to another org. */
export const switchOrg = async (orgId: string): Promise<unknown> => {
  const { data } = await axiosInstance.post(Platform_Switch_Org_Url, { org_id: orgId });
  return data;
};
